import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Card, Flex, QueryLoader, Text, toast } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useGetDocumentUploadInviteDetailsQuery } from "../../../graphql/supergraph.index";
import { DocumentsUploadedByInvitedUser } from "../../../graphql/supergraphOperations";
import useGuestDocumentProcessor from "../../../hooks/documents/useGuestDocumentProcessor";
import { getErrorMessage } from "../../../utils/error_parser";
import { clientContext } from "../../../utils/graphql_utils";
import { Celebration } from "../../common/Celebration";
import {
  textColorNeutral700,
  textColorNeutral800,
  textColorNeutral900,
} from "../../common/style_vanilla.css";
import { mainContentWrapper } from "../../style_vanilla.css";
import { DocumentCollector } from "../DocumentCollector";
import { DocumentData } from "../DocumentCollector/types";
import { formatDate } from "../utils";
import { dateDisplay, inviteSummaryWrapper } from "./style_vanilla.css";
import { hasMissingGuestDocuments, mapGuestDocumentsToFormProps } from "./utils";

export const DocumentCollectorGuest = () => {
  const [showCompleted, setShowCompleted] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const documentUploadInviteResult = useGetDocumentUploadInviteDetailsQuery({
    context: clientContext(),
    variables: { emailToken: token as string },
  });
  const { processDocuments, loading } = useGuestDocumentProcessor(token as string);

  const documentUploadInviteDetails =
    documentUploadInviteResult.data?.publicGetDocumentUploadInviteDetails;

  const documentDetails = useMemo(
    () =>
      mapGuestDocumentsToFormProps(
        documentUploadInviteDetails?.rulesEngine?.documentTypes || [],
        (documentUploadInviteDetails?.uploadedDocuments ||
          []) as DocumentsUploadedByInvitedUser[],
      ),
    [
      documentUploadInviteDetails?.rulesEngine?.documentTypes,
      documentUploadInviteDetails?.uploadedDocuments,
    ],
  );

  /**
   * Process documents and handle success/error feedback
   * @param formValues - The document form values to process
   */
  const handleDocumentProcessing = useCallback(
    (replacedFiles: DocumentData[], newUploadFiles: DocumentData[]) => {
      processDocuments(replacedFiles, newUploadFiles)
        .then(() => {
          toast.success("Documents updated successfully");
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
        });
    },
    [processDocuments],
  );

  useEffect(() => {
    setShowCompleted(
      hasMissingGuestDocuments(
        documentUploadInviteDetails?.rulesEngine?.documentTypes || [],
        (documentUploadInviteDetails?.uploadedDocuments ||
          []) as DocumentsUploadedByInvitedUser[],
      ) === false,
    );
  }, [documentUploadInviteDetails]);

  return (
    <QueryLoader result={documentUploadInviteResult}>
      {showCompleted ? (
        <Celebration />
      ) : (
        <Card
          className={mainContentWrapper}
          title={`Documents for ${documentUploadInviteDetails?.complianceProfile?.profileName}`}
        >
          <Text
            variant="regular-md"
            className={textColorNeutral800}
          >{`${documentUploadInviteDetails?.subscriberName} (${documentUploadInviteDetails?.subscriberEmailAddress}) has invited you to provide some information.`}</Text>
          <Flex className={inviteSummaryWrapper}>
            <Flex justifyContent="space-between" gap={Sizes.XS}>
              <Text variant="regular-md" lines={1} className={textColorNeutral900}>
                Message from
              </Text>
              <Text variant="strong-md" lines={1} className={textColorNeutral900}>
                {documentUploadInviteDetails?.subscriberName}
              </Text>
              <Text variant="regular-sm" className={dateDisplay}>
                {formatDate(documentUploadInviteDetails?.emailInviteTimestamp as string)}
              </Text>
            </Flex>
            <Text variant="regular-md" className={textColorNeutral700}>
              {documentUploadInviteDetails?.message}
            </Text>
          </Flex>
          <DocumentCollector
            isSubmitting={loading}
            documentDetails={documentDetails}
            onComplete={handleDocumentProcessing}
          />
        </Card>
      )}
    </QueryLoader>
  );
};
